import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';

import { Room } from '../../../../../core/models/room';
import { Calendar } from '../../../../../core/models/calendar';
import { Event } from '../../../../../core/interfaces/event';
import { SiteService } from './site.service';
import { HolidayService, HolidayInfo } from './holiday.service';
import { TimezoneService } from './timezone.service';
import { EventType } from '../../../../../core/enums/event-type';

import {
  CALENDAR_COLORS,
  usesLegendsDarkGrayTheme,
} from '../../core/constants/calendar-colors';
import { CalendarConfig } from '../interfaces/calendarConfiguration';
import {
  CalendarEvent,
  CreateCalendarDto,
} from '../../../../../core/interfaces/calendar';

import {
  CalendarDayConfig,
  CalendarDayEventsConfig,
} from '../interfaces/dayConfiguration';
import {
  pdfTableBody,
  pdfTableContent,
  pdfTableCell,
  pdfLayout,
  pdfStack,
} from '../interfaces/pdfMake/pdfTables';

import { Announcement } from '@models/announcement'; // to support CMS event calendar UI warnings
import { lastValueFrom } from 'rxjs';
import {
  endOfMonth,
  getDaysInMonth,
  parseISO,
  startOfMonth,
  format as formatDate,
  getYear,
  getMonth,
} from 'date-fns';
import { format } from 'date-fns-tz';
export interface CalendarFormat {
  rowsRequired: number;
  legendAtEndOfMonth: boolean;
  disclaimerAtEndOfMonth: boolean;
  hasLegendOnlyRow?: boolean; // True if legend/disclaimer are in a separate row with no date cells
}

@Injectable({
  providedIn: 'root',
})
export class CalendarService {
  private calendarEvents: { [day: number]: Array<Event> } = {};
  public maxEventsPerDayIn5RowMonth = 10;
  public maxEventsPerDayIn6RowMonth = 8;
  public legendDaysNeeded = 3;
  public readonly defaultMaxNumCharsPerLine = 18;

  // PDFmake adds undisclosed padding to table cells. We often reset top/bottom, left/right can be configured following this pattern.
  public tableLayoutPaddingReset: Partial<pdfLayout> = {
    paddingTop: function (i, node) {
      return 0;
    },
    paddingBottom: function (i, node) {
      return 0;
    },
  };

  public marginForVerticalCentering = {
    1: 'oneLineMargin',
    2: 'twoLineMargin',
    3: 'threeLineMargin',
    4: 'fourLineMargin',
    5: 'fiveLineMargin',
  };

  public smallMarginForVerticalCentering = {
    1: 'oneLineMarginSM',
    2: 'twoLineMarginSM',
    3: 'threeLineMarginSM',
    4: 'fourLineMarginSM',
    5: 'fiveLineMarginSM',
  };

  constructor(
    private http: HttpClient,
    private holidayService: HolidayService,
    private siteService: SiteService,
    private timezoneService: TimezoneService,
  ) {}

  /**
   * Returns the max number of events per day in that month,
   * as determined by what will fit in the print calendar
   *
   * @param dayInMonth dateTime representing any day in the month
   */
  public async getMaxEventsPerDayInMonth(
    dayInMonth: Date,
    timezone?: string,
  ): Promise<number> {
    const rowsThisMonth = await this.evaluateCalendarFormatForMonth(
      dayInMonth,
      timezone,
    );
    return rowsThisMonth.rowsRequired === 5
      ? this.maxEventsPerDayIn5RowMonth
      : this.maxEventsPerDayIn6RowMonth;
  }

  private containsSymbolCharacter(text: string): boolean {
    if (text.includes('®') || text.includes('©') || text.includes('™') || text.includes('℠') || /\(SM\)/i.test(text)) {
      return true;
    }
    return false;
  }

  private styleSymbols(
    event: Event | Announcement,
  ): Array<{ text: string; sup?: boolean }> {
    const label = event.title.replace(/\(SM\)/gi, '℠');
    let splitString = [label];
    if (label.includes('®')) {
      splitString = splitString.flatMap((item) => item.split(/(®)/g));
    }
    if (label.includes('©')) {
      splitString = splitString.flatMap((item) => item.split(/(©)/g));
    }
    if (label.includes('™')) {
      splitString = splitString.flatMap((item) => item.split(/(™)/g));
    }
    if (label.includes('℠')) {
      splitString = splitString.flatMap((item) => item.split(/(℠)/g));
    }
    const charCollection = splitString
      .filter((label) => label.length !== 0)
      .map((label) => {
        if (label === '℠') {
          // FranklinGothic font does not fully render the ℠ Unicode character;
          // use 'SM' text in superscript position instead.
          return { text: 'SM', sup: true };
        } else if (this.containsSymbolCharacter(label)) {
          return {
            text: label,
            sup: true,
          };
        } else {
          return {
            text: label,
          };
        }
      });
    // @ts-ignore
    const room = event.room || event.locationShortName;
    if (room) {
      if (
        room.shortName &&
        room.shortName !== ' - ' &&
        room.shortName !== '-'
      ) {
        charCollection.push({
          text: `, ${room.shortName}`,
        });
      } else if (!room.shortName && room !== ' - ' && room !== '-') {
        charCollection.push({
          text: `, ${room}`,
        });
      }
    }
    return charCollection;
  }

  // given an event (or announcement) and a character limit, determine how many rows it should take
  // optionally: return a text collection with superscript styles
  public getCalendarEventListing(
    item: Announcement | Event | CalendarEvent,
    maxNumCharacter: number,
    getCharCollection?: boolean,
  ): {
    label: string;
    numLinesPerItem: number;
    charCollection: Array<{ text: string; sup?: boolean }>;
  } {
    let itemTitle;
    let charCollection = [];

    //@ts-ignore
    if (item.room) {
      const announcement = item as Announcement;

      if (
        getCharCollection &&
        this.containsSymbolCharacter(announcement.title)
      ) {
        charCollection = this.styleSymbols(announcement);
      }
      itemTitle = `${announcement.title}${
        announcement.room.shortName &&
        announcement.room.shortName !== ' - ' &&
        announcement.room.shortName !== '-'
          ? ', ' + announcement.room.shortName
          : ''
      }`;
    } else {
      let event;
      //@ts-ignore
      if (item?.original) {
        event = item as CalendarEvent;

        if (getCharCollection && this.containsSymbolCharacter(event.title)) {
          charCollection = this.styleSymbols(event.src);
        }
        if (event.locationShortName) {
          itemTitle = `${event.title}${
            event.locationShortName &&
            event.locationShortName !== ' - ' &&
            event.locationShortName !== '-'
              ? ', ' + event.locationShortName
              : ''
          }`;
        } else {
          itemTitle = `${event.title}${
            event.original?.room?.short &&
            event.original?.room?.short !== ' - ' &&
            event.original?.room?.short !== '-'
              ? ', ' + event.original.room.short
              : ''
          }`;
        }

        //@ts-ignore
      } else if (item?.fullEvent) {
        event = item as Event;

        if (getCharCollection && this.containsSymbolCharacter(event.title)) {
          charCollection = this.styleSymbols(event.fullEvent);
        }

        if (event.locationShortName) {
          itemTitle = `${event.title}${
            event.locationShortName &&
            event.locationShortName !== ' - ' &&
            event.locationShortName !== '-'
              ? ', ' + event.locationShortName
              : ''
          }`;
        } else {
          itemTitle = `${event.title}${
            event.fullEvent?.room?.shortName &&
            event.fullEvent?.room?.shortName !== ' - ' &&
            event.fullEvent?.room?.shortName !== '-'
              ? ', ' + event.fullEvent.room.shortName
              : ''
          }`;
        }
      }
    }

    const originalLength = itemTitle.length;
    let numLinesPerItem = this.evaluateTextWrapOverflow(
      originalLength / maxNumCharacter,
    );

    if (originalLength > maxNumCharacter) {
      const titleChunk = itemTitle.match(
        new RegExp(
          `\\b[\\w+\\s]{${
            maxNumCharacter - 4
          },${maxNumCharacter}}.?(?=\\s)|.+$`,
          'g',
        ),
      );
      const titleChunkNoWhitespace = titleChunk.map((line: string) =>
        line.trim(),
      );
      itemTitle = titleChunkNoWhitespace.join('\n');

      // Count actual lines, but also check if any line exceeds maxNumCharacter
      // because pdfmake will wrap those further
      let actualLines = titleChunkNoWhitespace.length;
      for (const line of titleChunkNoWhitespace) {
        if (line.length > maxNumCharacter) {
          // This line will wrap again in pdfmake, add extra lines
          actualLines += Math.ceil(line.length / maxNumCharacter) - 1;
        }
      }

      if (actualLines > numLinesPerItem) {
        numLinesPerItem = actualLines;
      }
    }

    return { label: itemTitle, numLinesPerItem, charCollection };
  }

  /**
   * Given an array of events (or announcements), character and row restrictions, determine
   * which items will make it to the calendar or not.
   */
  public getCalendarEventsIncludedExcluded(
    events: Array<Announcement | Event>,
    maxNumCharacter,
    maxNumRows,
  ) {
    let eventLists = {
      included: [],
      excluded: [],
    };
    let linesPerItem = [];
    let tempCount = maxNumRows;
    events.forEach((event) => {
      const details = this.getCalendarEventListing(event, maxNumCharacter);
      let counter = tempCount - details.numLinesPerItem;

      if (counter >= 0) {
        eventLists.included.push(event);
        tempCount = counter;
      } else {
        eventLists.excluded.push(event);
        tempCount = counter;
      }
      linesPerItem.push(details.numLinesPerItem);
    });

    return { sorted: eventLists, linesPerItem };
  }

  public evaluateTextWrapOverflow(numLinesPerEvent): number {
    if (numLinesPerEvent < 1) {
      // all items must take at least one line
      numLinesPerEvent = 1;
    } else {
      numLinesPerEvent = Math.ceil(numLinesPerEvent);
    }

    return numLinesPerEvent;
  }

  /**
   * Returns a CalendarFormat interface object, describing
   * the number of rows this month's calendar will have
   * and whether the legend and disclaimer text will be
   * displayed at the beginning or end of the calendar
   *
   * @param date - Date representing any day in the month
   */
  public async evaluateCalendarFormatForMonth(
    date: Date,
    timezone?: string,
  ): Promise<CalendarFormat> {
    // For calendar format evaluation, we need the semantic month/year, NOT timezone-converted dates
    // The input 'date' represents which month the user selected, not a specific timestamp
    const tz = timezone || this.siteService.currentSite?.timezone;

    // Use the semantic month/year from the input date directly
    // Don't convert to site timezone as this would shift the month boundaries
    const year = getYear(date);
    const month = getMonth(date); // 0-indexed
    const dateInSiteTimezone = new Date(year, month, 1); // First of the month in local time

    const daysInMonth = getDaysInMonth(dateInSiteTimezone);
    const firstOfMonth = startOfMonth(dateInSiteTimezone);
    const lastOfMonth = endOfMonth(dateInSiteTimezone);
    const startDow = firstOfMonth.getDay(); // 0=Sunday, 1=Monday, etc.
    const endDow = lastOfMonth.getDay(); // 0=Sunday, 1=Monday, etc.
    const daysBefore = startDow;
    const daysAfter = 7 - endDow - 1;
    let trailingDaysNeeded = 4;
    this.legendDaysNeeded = 3;
    const disclaimerDaysNeeded = 1;
    const totalDays = daysBefore + daysInMonth + daysAfter;
    let rowsRequired: number;
    let legendAtEndOfMonth: boolean;
    let disclaimerAtEndOfMonth: boolean;
    if (totalDays > 7 * 5) {
      rowsRequired = 6;
    } else {
      rowsRequired = 5;
    }

    /**
     * VERY specific scenario: 31 day months that start on a Tuesday have only 2 free squares at the start and end.
     * If there are not many room names in use, we can reduce the size of the legend to a width of 2 cols to avoid
     * adding an additional row to the month (which would mean fewer events fitting on the calendar)
     * [SC-3685](https://cloudburst.atlassian.net/browse/SC-3685)
     **/
    if (
      trailingDaysNeeded > daysAfter &&
      trailingDaysNeeded > daysBefore &&
      this.legendDaysNeeded > daysBefore &&
      this.legendDaysNeeded > daysAfter
    ) {
      // Use original date and timezone for month-based queries
      this.calendarEvents = await this.getEventsForCalendar(
        this.siteService.currentSiteId,
        firstOfMonth,
        tz,
      );

      let filteredRoomIds = [];
      let activeRooms = [];

      // Determine which rooms are in active use for this month of events
      Object.keys(this.calendarEvents).forEach((day) => {
        this.calendarEvents[day].filter((events) => {
          if (filteredRoomIds.includes(events?.fullEvent?.roomId)) {
            return;
          } else if (events?.fullEvent?.roomId) {
            filteredRoomIds.push(events?.fullEvent?.roomId);
            activeRooms.push(events?.fullEvent?.room);
          }
        });
      });
      /**
       * TODO: MAGIC NUMBER SEVEN.
       * Seven rooms fit in one column of the legend in a 5 row month at this font size and paper size.
       * At the time of writing, we never want less than two columns or more than three.
       * May be necessary to determine how many columns are needed more dynamically later on.
       * **/
      this.legendDaysNeeded = Math.ceil(activeRooms.length / 7) <= 2 ? 2 : 3;

      trailingDaysNeeded = this.legendDaysNeeded + disclaimerDaysNeeded;
    }

    let hasLegendOnlyRow = false;

    if (trailingDaysNeeded <= daysAfter) {
      legendAtEndOfMonth = true;
      disclaimerAtEndOfMonth = true;
    } else if (trailingDaysNeeded <= daysBefore) {
      legendAtEndOfMonth = false;
      disclaimerAtEndOfMonth = false;
    } else if (
      this.legendDaysNeeded <= daysAfter &&
      disclaimerDaysNeeded <= daysBefore
    ) {
      legendAtEndOfMonth = true;
      disclaimerAtEndOfMonth = false;
    } else if (
      this.legendDaysNeeded <= daysBefore &&
      disclaimerDaysNeeded <= daysAfter
    ) {
      legendAtEndOfMonth = false;
      disclaimerAtEndOfMonth = true;
    } else {
      /**
       * This condition happens when:
       * - There are less than 3 squares of available space at the start AND end of the month
       * - AND there are 3 columns worth of active room names to display in the legend
       *
       * Instead of increasing rowsRequired to 6 (which reduces lines per day from 10 to 8),
       * we set hasLegendOnlyRow=true so the PDF layout can allocate a separate shorter row
       * for the legend while keeping the date rows at full height with 10 lines per day.
       **/
      legendAtEndOfMonth = true;
      disclaimerAtEndOfMonth = true;
      hasLegendOnlyRow = true;
    }

    /**
     * Detect if legend/disclaimer will be placed in a separate legend-only row
     * This happens when both are at the end and there are more than 3 cells filled
     * in the last row (daysAfter <= 3), matching the logic in getCalendar() line 1318
     **/
    if (legendAtEndOfMonth && disclaimerAtEndOfMonth && daysAfter <= 3) {
      hasLegendOnlyRow = true;
    }

    return {
      rowsRequired,
      legendAtEndOfMonth,
      disclaimerAtEndOfMonth,
      hasLegendOnlyRow,
    };
  }

  private getEventsForCalendar(
    siteId: number,
    originalDate: Date,
    timezone?: string,
  ): Promise<{ [day: number]: Array<Event> }>;
  private getEventsForCalendar(
    siteId: number,
    startDateUTC: Date,
    endDateUTC: Date,
  ): Promise<{ [day: number]: Array<Event> }>;
  private getEventsForCalendar(
    siteId: number,
    dateOrStart: Date,
    timezoneOrEnd?: string | Date,
  ): Promise<{ [day: number]: Array<Event> }> {
    let queryParams: URLSearchParams;

    if (typeof timezoneOrEnd === 'string' || timezoneOrEnd === undefined) {
      // New signature: getEventsForCalendar(siteId, originalDate, timezone?)
      // Use month/year parameters to avoid timezone issues
      const year = getYear(dateOrStart);
      const month = getMonth(dateOrStart); // 0-indexed
      queryParams = new URLSearchParams({
        month: month.toString(),
        year: year.toString(),
      });
    } else {
      // Legacy signature: getEventsForCalendar(siteId, startDateUTC, endDateUTC)
      const startDateUTC = dateOrStart;
      const endDateUTC = timezoneOrEnd as Date;

      // Check if this represents a full month range to use month parameters
      const isFullMonth = this.isFullMonthRange(startDateUTC, endDateUTC);

      if (isFullMonth) {
        // Use month/year parameters to avoid timezone issues
        const year = getYear(startDateUTC);
        const month = getMonth(startDateUTC); // 0-indexed
        queryParams = new URLSearchParams({
          month: month.toString(),
          year: year.toString(),
        });
      } else {
        // Use ISO date strings for precise date ranges
        queryParams = new URLSearchParams({
          eventStartISO: formatDate(startDateUTC, 'yyyy-MM-dd'),
          eventEndISO: formatDate(endDateUTC, 'yyyy-MM-dd'),
        });
      }
    }

    const url = environment.apiv3Url.concat(
      '/calendars/print/',
      siteId.toString(),
      '?',
      queryParams.toString(),
    );

    try {
      return lastValueFrom(this.http.get<{ [day: number]: Array<Event> }>(url));
    } catch (err) {
      console.error('ERROR', err);
      return;
    }
  }

  /**
   * Check if the date range represents a full month
   */
  private isFullMonthRange(startDate: Date, endDate: Date): boolean {
    // Check if start is the first day of a month and end is the last day of the same month
    const startIsFirstOfMonth = startDate.getDate() === 1;
    const endIsLastOfMonth =
      endDate.getDate() ===
      new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0).getDate();
    const sameMonth =
      startDate.getMonth() === endDate.getMonth() &&
      startDate.getFullYear() === endDate.getFullYear();

    return startIsFirstOfMonth && endIsLastOfMonth && sameMonth;
  }

  private buildCalendarDay(
    calendarDayConfig: CalendarDayConfig,
  ): Array<pdfTableContent> | pdfTableContent {
    const {
      day,
      holidays,
      calRows,
      timezone,
      numberOfEventsPerDay,
      numLinesPerDay,
      maxNumCharacter,
      templateMode,
      fontOverride,
      eventGridHeight,
      verticallyCentered,
      useSmallFont,
    } = calendarDayConfig;

    if (this.calendarEvents[calendarDayConfig.day]) {
      // If there are events, list them on the day
      return this.buildDayWithEvents({
        day,
        events: this.calendarEvents[day],
        holidays,
        calRows,
        timezone,
        numberOfEventsPerDay,
        numLinesPerDay,
        maxNumCharacter,
        templateMode,
        fontOverride,
        verticallyCentered,
        eventGridHeight,
        useSmallFont,
      });
    } else if (holidays) {
      return this.buildDayWithHolidays({
        day,
        holidays,
        eventBody: null,
        verticallyCentered,
        eventGridHeight,
        templateMode,
      });
    } else {
      // Else just the number
      return this.buildDayWithoutHoliday({
        day,
        eventBody: null,
        verticallyCentered,
        eventGridHeight,
        templateMode,
      });
    }
  }

  private buildDayWithoutHoliday(config: {
    day: number;
    eventBody;
    verticallyCentered?: boolean;
    eventGridHeight?: number;
    templateMode?: boolean;
  }): pdfTableContent {
    const {
      day,
      eventBody,
      verticallyCentered,
      eventGridHeight,
      templateMode,
    } = config;
    const nestedTableBody = [
      {
        text: day,
        style: 'day',
      },
    ];
    const nestedHeadingTable = {
      table: {
        headerows: 0,
        body: [nestedTableBody],
        widths: ['*'],
      },
      layout: {
        hLineWidth: (i, node) => {
          return 0;
        },
        vLineWidth: (i, node) => {
          return 0;
        },
        defaultBorder: templateMode ? true : false,
      },
      style: 'nestedTable',
    };

    return {
      table: {
        body: [[nestedHeadingTable], [eventBody]],
        widths: ['*'],
      },
      layout: {
        ...this.tableLayoutPaddingReset,
        paddingLeft: function (i, node) {
          return 0;
        },
        paddingRight: function (i, node) {
          return 0;
        },
        hLineWidth: (i, node) => {
          return 0;
        },
        vLineWidth: (i, node) => {
          return 0;
        },
        defaultBorder: templateMode ? true : false,
      },
    };
  }

  private buildDayWithHolidays(config: {
    day: number;
    eventBody;
    holidays: Array<HolidayInfo>;
    verticallyCentered?: boolean;
    eventGridHeight?: number;
    templateMode?: boolean;
  }): pdfTableContent | Array<pdfTableContent> {
    const {
      day,
      holidays,
      eventBody,
      verticallyCentered,
      eventGridHeight,
      templateMode,
    } = config;
    if (holidays.length === 0) {
      return this.buildDayWithoutHoliday({ day, eventBody, templateMode });
    }
    const firstHoliday = holidays[0]; // Ignores all but the first holiday
    const nestedTableBody = [
      {
        text: firstHoliday.name.toUpperCase(),
        style: 'holiday',
        noWrap: false,
      },
      {
        text: day,
        style: 'day',
        noWrap: true,
        layout: {
          ...this.tableLayoutPaddingReset,
          paddingBottom: function (i, node) {
            return 0;
          },
          paddingLeft: function (i, node) {
            return 0;
          },
          paddingTop: function (i, node) {
            return 0;
          },
          paddingRight: function (i, node) {
            return 0;
          },
          hLineWidth: (i, node) => {
            return 0;
          },
          vLineWidth: (i, node) => {
            return 0;
          },
        },
      },
    ];

    const nestedHeadingTable = {
      table: {
        headerows: 0,
        body: [nestedTableBody],
        widths: ['*', 'auto'],
      },
      layout: {
        ...this.tableLayoutPaddingReset,
        paddingBottom: function (i, node) {
          return 0;
        },
        paddingLeft: function (i, node) {
          return 2 * 0.75;
        }, // padding left of holiday
        paddingTop: function (i, node) {
          return 2 * 0.75;
        }, // padding top of holiday
        paddingRight: function (i, node) {
          return 0;
        }, // padding right of day
        hLineWidth: (i, node) => {
          return 0;
        },
        vLineWidth: (i, node) => {
          return 0;
        },
        defaultBorder: templateMode ? true : false,
      },
      style: 'nestedTable',
    };

    return {
      table: {
        body: [[nestedHeadingTable], [eventBody]],
        widths: ['*'],
      },
      layout: {
        ...this.tableLayoutPaddingReset,
        paddingLeft: function (i, node) {
          return 0;
        },
        paddingRight: function (i, node) {
          return 0;
        },
        hLineWidth: (i, node) => {
          return 0;
        },
        vLineWidth: (i, node) => {
          return 0;
        },
        defaultBorder: templateMode ? true : false,
      },
    };
  }

  private buildDayWithEvents(
    calendarDayEventsConfig: CalendarDayEventsConfig,
  ): Array<pdfTableContent> | pdfTableContent {
    const {
      day,
      events,
      holidays,
      calRows,
      timezone,
      numLinesPerDay,
      maxNumCharacter,
      templateMode,
      fontOverride,
      verticallyCentered,
      eventGridHeight,
      useSmallFont,
    } = calendarDayEventsConfig;

    return this.buildDayWithHolidays({
      day,
      holidays,
      eventBody: this.formatDay({
        day,
        events,
        calRows,
        maxNumCharacter,
        numLinesPerDay: numLinesPerDay,
        timezone,
        numberOfEventsPerDay: numLinesPerDay?.regular,
        templateMode,
        verticallyCentered,
        eventGridHeight,
        useSmallFont,
      }),
      templateMode,
    });
  }

  /**
   * Determines whether a single day's events require small font to fit.
   * Used as a pre-pass to ensure consistent font size across all days.
   */
  public dayNeedsSmallFont(
    events: Array<Event>,
    numLinesPerDay: { small: number; regular: number },
    maxNumCharacter: { small: number; regular: number },
    calRows?: number,
  ): boolean {
    if (!events || events.length === 0) return false;
    let maxNumLines = numLinesPerDay
      ? numLinesPerDay.regular
      : calRows === 5
        ? this.maxEventsPerDayIn5RowMonth
        : this.maxEventsPerDayIn6RowMonth;
    const maxNumChars = maxNumCharacter
      ? maxNumCharacter.regular
      : this.defaultMaxNumCharsPerLine;
    let eventsToShow = maxNumLines;
    for (const event of events) {
      const listing = this.getCalendarEventListing(event, maxNumChars);
      const numLinesPerEvent = this.evaluateTextWrapOverflow(listing.numLinesPerItem);
      eventsToShow = eventsToShow - numLinesPerEvent;
      if (eventsToShow < 0) {
        return true;
      }
    }
    return false;
  }

  /**
   * Returns nested table body containing events for the day and their times
   * Limits number of events to monthly max per day, depending on number of rows in month
   * Truncates event names to fit on one line and does not allow wrapping
   * @param day
   * @param events
   * @param calRows
   */
  private formatDay(calendarDayEventsConfig: CalendarDayEventsConfig) {
    const {
      events,
      calRows,
      timezone,
      numLinesPerDay,
      templateMode,
      fontSizeStyle,
      maxNumCharacter,
      verticallyCentered,
    } = calendarDayEventsConfig;
    let maxNumLines: number;
    let maxNumChars: number;
    let eventsToShow: number;
    // Use the month-wide consistent font size decision if provided,
    // otherwise fall back to per-day calculation for backward compatibility
    let useSmallFont = calendarDayEventsConfig.useSmallFont ?? false;
    if (numLinesPerDay) {
      maxNumLines = useSmallFont ? numLinesPerDay.small : numLinesPerDay.regular;
      maxNumChars = useSmallFont
        ? maxNumCharacter.small
        : maxNumCharacter.regular;
    } else if (calRows === 5) {
      maxNumLines = this.maxEventsPerDayIn5RowMonth;
    } else {
      maxNumLines = this.maxEventsPerDayIn6RowMonth;
    }
    if (!maxNumChars && maxNumCharacter) {
      maxNumChars = useSmallFont
        ? maxNumCharacter.small
        : maxNumCharacter.regular;
    }
    const nestedTableBody = [];

    // When useSmallFont is already set by the month-wide pre-pass,
    // skip the per-day font size evaluation to ensure consistency
    if (calendarDayEventsConfig.useSmallFont === undefined) {
      // Legacy per-day font size evaluation (fallback)
      eventsToShow = maxNumLines;

      events.forEach((event, index) => {
        const listing = this.getCalendarEventListing(event, maxNumChars);
        const numLinesPerEvent = this.evaluateTextWrapOverflow(
          listing.numLinesPerItem,
        );
        eventsToShow = eventsToShow - numLinesPerEvent;
        if (eventsToShow < 0 && !useSmallFont && numLinesPerDay) {
          useSmallFont = true;
          maxNumLines = numLinesPerDay.small;
          maxNumChars = maxNumCharacter.small;
          eventsToShow = maxNumLines;
        }
      });

      // If we switched to small font, recalculate all events with new parameters
      if (useSmallFont) {
        eventsToShow = maxNumLines;
        for (let i = 0; i < events.length; i++) {
          const listing = this.getCalendarEventListing(events[i], maxNumChars);
          const numLinesPerEvent = this.evaluateTextWrapOverflow(
            listing.numLinesPerItem,
          );
          eventsToShow = eventsToShow - numLinesPerEvent;
        }
      }
    }

    events.sort((a, b) => {
      const dateA = new Date(a.eventStart);
      const dateB = new Date(b.eventStart);
      return dateA.getTime() - dateB.getTime();
    });

    let linesRemaining = maxNumLines;

    // Loop through events (not lines) and stop when we run out of space
    for (let i = 0; i < events.length && linesRemaining > 0; i++) {
      if (events[i]) {
        const style = ['eventListing'];
        const eventTypeStyle = [];
        const eventTimeStyle = ['time'];

        // Event Type Styling

        if (EventType[events[i].eventType] === EventType.SpecialEvent) {
          eventTypeStyle.push('bold');
        }

        // all day events display in bold with no time listed
        if (events[i].allDay && !eventTypeStyle.includes('bold')) {
          eventTypeStyle.push('bold');
        }

        if (fontSizeStyle) {
          eventTypeStyle.push(fontSizeStyle);
          eventTimeStyle.push(fontSizeStyle);
        }

        // check for superscript formatting
        const listing = this.getCalendarEventListing(
          events[i],
          maxNumChars,
          true,
        );

        const numLinesPerEvent = this.evaluateTextWrapOverflow(
          listing.numLinesPerItem,
        );

        linesRemaining = linesRemaining - numLinesPerEvent;

        // if this event would run over, don't include it
        // allows us to check for another event later that could fit
        if (linesRemaining >= 0) {
          nestedTableBody.push([
            {
              text: events[i].allDay
                ? ''
                : (() => {
                    // Properly convert UTC time to site's timezone
                    const eventStartDate = parseISO(
                      events[i].eventStart.toString(),
                    );
                    const startInSiteTimezone =
                      this.timezoneService.fromApiDate(
                        eventStartDate,
                        timezone,
                      );
                    return format(startInSiteTimezone, 'h:mm', {
                      timeZone: timezone,
                    });
                  })(),
              style: [
                ...style,
                ...eventTimeStyle,
                useSmallFont ? 'smallFont' : '',
              ],
              noWrap: true,
            },
            {
              text:
                listing?.charCollection.length > 0
                  ? listing.charCollection
                  : listing.label,
              style: [
                ...style,
                ...eventTypeStyle,
                useSmallFont ? 'smallFont' : '',
              ],
              layout: {
                paddingBottom: function (i, node) {
                  return 0;
                },
                paddingLeft: function (i, node) {
                  return 0;
                }, // padding left of holiday
                paddingTop: function (i, node) {
                  return 0;
                }, // padding top of holiday
                paddingRight: function (i, node) {
                  return 0;
                },
                hLineWidth: (i, node) => {
                  return 0;
                },
                vLineWidth: (i, node) => {
                  return 0;
                },
              },
              noWrap: false,
            },
          ]);
        }
      }
    }
    let eventListStyle: string | Array<string> = 'nestedTable';
    // if enabled and there is room for additional lines, vertically center events
    if (verticallyCentered && events.length > 0 && linesRemaining > 0) {
      eventListStyle = useSmallFont
        ? [this.smallMarginForVerticalCentering[linesRemaining], 'dayRowOffset']
        : [this.marginForVerticalCentering[linesRemaining], 'dayRowOffset'];
    }
    // Table styling for event time and name

    // Time column width varies based on font size, but must be a consistent width regardless of content to support alignment for all-day events
    const timeColumnWidth = useSmallFont ? 28 : 35;
    return {
      table: {
        headerows: 0,
        body: nestedTableBody,
        widths: [timeColumnWidth, '*'],
      },
      layout: {
        ...this.tableLayoutPaddingReset,
        paddingLeft: function (i, node) {
          return 2 * 0.75;
        }, // padding left of time and event title
        hLineWidth: (i, node) => {
          return 0;
        },
        vLineWidth: (i, node) => {
          return 0;
        },
        defaultBorder: templateMode ? true : false,
      },
      style: eventListStyle,
    };
  }

  private formatLegendShortName(room: Room): string {
    return room.shortName;
  }

  // TODO: calculate name length dynamically based on width
  private formatLegendName(room: Room): string {
    let longName: string;
    if (room.name.length >= 22) {
      longName = room.name.substring(0, 21).trim();
    } else {
      longName = room.name;
    }

    return longName;
  }

  private formatLegend(legendConfig: {
    roomData: Room[];
    maxRooms: number;
    templateMode?: boolean;
  }): pdfStack {
    const { roomData, maxRooms, templateMode } = legendConfig;
    if (!roomData || roomData.length === 0) {
      return [{ text: '', style: 'locationHeader' }];
    }
    const nPerColumn = this.legendDaysNeeded ? this.legendDaysNeeded : 3;
    const rows = [];

    // Filter out mobile app name
    const filteredRoomData = roomData.filter((value) => {
      return value.name !== 'GiGi Mobile App';
    });

    if (filteredRoomData.length === 0) {
      return [{ text: '', style: 'locationHeader' }, {}];
    }

    // Max number of rooms we have room to display is 18 for 6 row months and 21 for 5 row
    const roomDataLength =
      filteredRoomData.length <= maxRooms ? filteredRoomData.length : maxRooms;
    for (let i = 0; i < Math.ceil(roomDataLength / nPerColumn); i++) {
      rows[i] = filteredRoomData.slice(nPerColumn * i, nPerColumn * (i + 1));
    }

    for (let i = 0; i < rows.length; i++) {
      const shorts = rows[i].map((row) => this.formatLegendShortName(row));
      const fulls = rows[i].map((row) => this.formatLegendName(row));

      rows[i] = [];
      for (let j = 0; j < shorts.length; j++) {
        if (fulls[j]) {
          rows[i].push({
            style: ['legendEntry'],
            text: `${fulls[j]}${shorts[j] ? `, ${shorts[j]}` : ''}`,
          });
        }
      }

      // if number of rooms is not divisible by n, add empty cells for proper cell formatting
      if (rows[i].length < nPerColumn) {
        for (let k = rows[i].length; k < nPerColumn; k++) {
          rows[i].push({});
        }
      }
    }

    let cols = [];
    for (let x = nPerColumn; x > 0; x--) {
      cols.push('*');
    }
    return [
      { text: 'LOCATIONS', style: 'locationHeader' },
      {
        table: {
          body: rows,
          widths: cols,
        },
        layout: {
          ...this.tableLayoutPaddingReset,
          paddingLeft: function (i, node) {
            return 0;
          }, // padding left of location columns
          hLineWidth: (i, node) => {
            return 0;
          },
          vLineWidth: (i, node) => {
            return 0;
          },
          defaultBorder: templateMode ? true : false,
        },
        style: 'nestedTable',
      },
    ];
  }

  private async buildLegendSection(legendConfig: {
    siteId: number;
    maxRooms: number;
    activeRooms?: Array<Room>;
    templateMode?: boolean;
  }): Promise<pdfTableCell> {
    const { siteId, maxRooms, activeRooms, templateMode } = legendConfig;
    const rooms = activeRooms?.length > 0 ? activeRooms : [];
    const legendRooms = rooms.filter((room) => room.name !== 'Custom Room');
    const sortedLegendRooms = legendRooms.sort((a, b) =>
      a.name > b.name ? 1 : -1,
    ); // sorts  objects in legendRooms array on name property
    const roomLegend = this.formatLegend({
      roomData: sortedLegendRooms,
      maxRooms,
      templateMode,
    });
    return {
      colSpan: this.legendDaysNeeded,
      stack: roomLegend,
      style: 'roomLegend',
    };
  }

  private buildSupplementalInfo(customText: string): pdfTableCell {
    return {
      stack: [
        { text: 'Due to calendar space, ', style: 'disclaimer' },
        { text: 'all programs may not be', style: 'disclaimer3' },
        { text: 'reflected.', style: 'disclaimer2' },
        { text: 'To stay up to date with', style: 'disclaimer' },
        { text: 'all events, please visit', style: 'disclaimer3' },
        {
          text: ['the GiGi Assistant', { text: '®', sup: true }, 'app.'],
          style: 'disclaimer3',
        },
      ],
      style: 'supplementalBlock',
    };
  }

  public async getCalendar(
    calendarConfig: CalendarConfig,
  ): Promise<Array<pdfTableBody>> {
    const {
      siteId,
      timezone,
      monthIndex,
      year,
      monthDate,
      customText,
      rows,
      displayLegendAtEnd,
      displayDisclaimerAtEnd,
      state,
      maxNumCharacter,
      numberOfEventsPerDay,
      numLinesPerDay,
      templateMode,
      maxRooms,
      fontOverride,
      eventGridHeight,
      verticallyCentered,
      subcategoryIds,
    } = calendarConfig;

    // Use semantic month/year for calendar structure to avoid timezone issues
    const calendarYear = getYear(monthDate);
    const calendarMonth = getMonth(monthDate); // 0-indexed

    // Create first and last day using semantic values, not timezone-converted dates
    const firstOfMonth = new Date(calendarYear, calendarMonth, 1);
    const lastOfMonth = new Date(calendarYear, calendarMonth + 1, 0); // Last day of month

    this.calendarEvents = await this.getEventsForCalendar(
      siteId,
      monthDate,
      timezone,
    );

    let filteredRoomIds = [];
    let activeRooms = [];

    // Determine which rooms are in active use for this month of events
    Object.keys(this.calendarEvents).forEach((day) => {
      let calendarDay = this.calendarEvents[day];
      // if we are passing category ids, filter them out here
      if (subcategoryIds) {
        calendarDay = calendarDay.filter((event) => {
          return subcategoryIds.includes(event?.fullEvent?.subcategory?.id);
        });
        if (calendarDay.length === 0) {
          delete this.calendarEvents[day];
        } else {
          this.calendarEvents[day] = calendarDay;
        }
      }
      calendarDay.forEach((events) => {
        if (filteredRoomIds.includes(events?.fullEvent?.roomId)) {
          // do nothing
        } else if (events?.fullEvent?.roomId) {
          filteredRoomIds.push(events?.fullEvent?.roomId);
          const currentRoom = events.fullEvent.room;
          if (currentRoom) activeRooms.push(currentRoom);
        }
      });
    });

    // Pre-pass: determine if ANY day in this month needs small font.
    // If so, use small font for ALL days to ensure consistent font size across the calendar.
    let useSmallFontGlobal: boolean | undefined = calendarConfig.useSmallFont;
    if (useSmallFontGlobal === undefined && numLinesPerDay && maxNumCharacter) {
      useSmallFontGlobal = false;
      for (const dayKey of Object.keys(this.calendarEvents)) {
        if (
          this.dayNeedsSmallFont(
            this.calendarEvents[dayKey],
            numLinesPerDay,
            maxNumCharacter,
            rows,
          )
        ) {
          useSmallFontGlobal = true;
          break;
        }
      }
    }

    const firstDayOfMonth: number = firstOfMonth.getDay();
    const lastDateOfMonth: number = lastOfMonth.getDate();
    // change for Legends on Lake Lorraine and Legends of Sarasota
    const mainFillColor = usesLegendsDarkGrayTheme(siteId)
      ? CALENDAR_COLORS.legendsDarkGray
      : CALENDAR_COLORS.blue;
    const emptyCellFillColor = CALENDAR_COLORS.white;

    const calendar: Array<pdfTableBody> = [
      [
        { text: 'SUNDAY', style: 'tableHeader', fillColor: mainFillColor },
        { text: 'MONDAY', style: 'tableHeader', fillColor: mainFillColor },
        { text: 'TUESDAY', style: 'tableHeader', fillColor: mainFillColor },
        { text: 'WEDNESDAY', style: 'tableHeader', fillColor: mainFillColor },
        { text: 'THURSDAY', style: 'tableHeader', fillColor: mainFillColor },
        { text: 'FRIDAY', style: 'tableHeader', fillColor: mainFillColor },
        { text: 'SATURDAY', style: 'tableHeader', fillColor: mainFillColor },
      ],
      [],
    ];

    let week = 0;
    calendar[1] = [];

    // Build pre-month cells
    if (firstDayOfMonth > 0 && !displayLegendAtEnd && displayDisclaimerAtEnd) {
      // Add legend to top
      calendar[1].push(
        await this.buildLegendSection({
          siteId,
          maxRooms,
          activeRooms,
          templateMode,
        }),
      );
      calendar[1].push({});
      calendar[1].push({});
    } else if (
      firstDayOfMonth > 0 &&
      displayLegendAtEnd &&
      !displayDisclaimerAtEnd
    ) {
      // Add disclaimer to top
      calendar[1].push(this.buildSupplementalInfo(customText));
    } else if (
      firstDayOfMonth > 0 &&
      !displayLegendAtEnd &&
      !displayDisclaimerAtEnd
    ) {
      // Add legend and disclaimer to top
      calendar[1].push(
        await this.buildLegendSection({
          siteId,
          maxRooms,
          activeRooms,
          templateMode,
        }),
      );
      calendar[1].push({});
      calendar[1].push({});
      calendar[1].push(this.buildSupplementalInfo(customText));
    }

    // Add colspan cell to fill out the week
    if (calendar[1].length < firstDayOfMonth - 1) {
      const extraCellsNeeded = firstDayOfMonth - calendar[1].length;
      const tableCell: pdfTableCell = {
        colSpan: extraCellsNeeded,
        text: '',
        style: 'day',
        fillColor: emptyCellFillColor,
      };
      calendar[1].push(tableCell);
      // Add cells that won't display because of colspan above
      for (let i = 1; i < extraCellsNeeded; i++) {
        calendar[1].push({});
      }
    } else if (calendar[1].length < firstDayOfMonth) {
      const tableCell: pdfTableCell = {
        text: '',
        style: 'day',
        fillColor: emptyCellFillColor,
      };
      calendar[1].push(tableCell);
    }

    // Build intermediate cells
    for (let day = 1; day <= lastDateOfMonth; day++) {
      const date: Date = new Date(calendarYear, calendarMonth, day);
      const holidays = this.holidayService.getHolidaysForDate(
        date,
        state === 'AZ' ? 'Daylight Saving' : null,
      ); // Arizona doesn't observe daylight savings, will be filtered out
      const dayOfWeek = Math.floor((firstDayOfMonth + day - 1) % 7) + 1;

      week = calendar.length - 1;
      const isEndOfWeek = dayOfWeek % 7 === 0;
      calendar[week].push(
        this.buildCalendarDay({
          day,
          holidays,
          calRows: rows,
          timezone,
          numberOfEventsPerDay,
          numLinesPerDay,
          maxNumCharacter,
          templateMode,
          fontOverride,
          eventGridHeight,
          verticallyCentered,
          useSmallFont: useSmallFontGlobal,
        }),
      );
      if (isEndOfWeek && day !== lastDateOfMonth) {
        calendar.push([]);
      }
    }

    // Build post-month cells
    let lastRow = calendar[calendar.length - 1];
    if (displayLegendAtEnd && displayDisclaimerAtEnd && lastRow.length > 3) {
      // If there isn't room for end content, add empty cells and another row
      for (let i = lastRow.length; i < 7; i++) {
        calendar[calendar.length - 1].push({
          text: '',
          fillColor: emptyCellFillColor,
        });
      }
      calendar.push([]);
    }

    lastRow = calendar[calendar.length - 1];

    if (displayLegendAtEnd) {
      const legendSection: any = await this.buildLegendSection({
        siteId,
        maxRooms,
        activeRooms,
        templateMode,
      });
      calendar[calendar.length - 1].push(legendSection);
      lastRow = calendar[calendar.length - 1];
      for (let i = 0; i < legendSection.colSpan - 1; i++) {
        calendar[calendar.length - 1].push({});
      }
    }

    if (displayDisclaimerAtEnd) {
      calendar[calendar.length - 1].push(
        this.buildSupplementalInfo(customText),
      );
    }

    // Add colspan cell to fill out the week
    if (lastRow.length < 6) {
      const extraCellsNeeded = 7 - lastRow.length;
      const tableCell: pdfTableCell = {
        colSpan: extraCellsNeeded,
        text: '',
        style: 'day',
        fillColor: emptyCellFillColor,
      };
      calendar[calendar.length - 1].push(tableCell);
      // Add cells that won't display because of colspan above
      for (let i = 1; i < extraCellsNeeded; i++) {
        calendar[calendar.length - 1].push({});
      }
    } else if (lastRow.length < 7) {
      const tableCell: pdfTableCell = {
        text: '',
        style: 'day',
        fillColor: emptyCellFillColor,
      };
      calendar[calendar.length - 1].push(tableCell);
    }

    return calendar;
  }

  /**
   * Send email alert for completed calendar.
   * @param calendar
   */
  public emailCalendar(calendar: CreateCalendarDto): Promise<Calendar> {
    const url = `${environment.apiv3Url}/calendars`;

    try {
      return <Promise<any>>lastValueFrom(this.http.post(url, calendar));
    } catch (err) {
      console.error('ERROR', err);
      return;
    }
  }
}
